import { useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { fetchOrderReservation } from "store/reservations"

const byBookedDate = (first, second) => (first.bookedDate || "").localeCompare(second.bookedDate || "")

// A reservation's experiences: the opened booking and, when it belongs to an order, the order's
// other bookings, each loaded as a reservation of its own, in date order. One that fails to load
// keeps its place with the error, so the others still show.
export const useOrderExperiences = (reservation) => {
  const dispatch = useDispatch()
  // uuid → { data, isLoading, error } for the order's other bookings.
  const [others, setOthers] = useState({})
  const orderBookings = reservation?.orderBookings || []
  const orderKey = orderBookings.map((booking) => booking.uuid).join(",")

  const loadExperience = useCallback(
    async (uuid) => {
      setOthers((prev) => ({ ...prev, [uuid]: { ...prev[uuid], isLoading: true, error: null } }))
      try {
        const data = await dispatch(fetchOrderReservation(uuid))
        setOthers((prev) => ({ ...prev, [uuid]: { data, isLoading: false, error: null } }))
      } catch (error) {
        setOthers((prev) => ({ ...prev, [uuid]: { ...prev[uuid], isLoading: false, error } }))
      }
    },
    [dispatch]
  )

  useEffect(() => {
    if (orderKey) orderKey.split(",").forEach(loadExperience)
  }, [orderKey, loadExperience])

  if (!reservation) return { experiences: [], isOrder: false, loadExperience }

  const experiences = [
    { uuid: reservation.uuid, bookedDate: reservation.bookedDate, data: reservation, isLoading: false, error: null },
    ...orderBookings.map((booking) => ({
      uuid: booking.uuid,
      bookedDate: booking.bookedDate,
      booking,
      ...(others[booking.uuid] || { isLoading: true })
    }))
  ].sort(byBookedDate)

  return { experiences, isOrder: orderBookings.length > 0, loadExperience }
}
